import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fundingListsAPI } from '../../services/api';
import AuthModal from '../auth/AuthModal';
import Tooltip from '../common/Tooltip';
import { usePostHog } from '@posthog/react';

const SaveButton = ({ funding, onSaveToggle }) => {
  const { isAuthenticated } = useAuth();
  const posthog = usePostHog();
  const fundingLookup = funding.slug || funding.id;
  const [isSaved, setIsSaved] = useState(funding.is_favourite || false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  // Set of list IDs that already contain this scheme
  const [memberLists, setMemberLists] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(null); // list id being toggled

  // Inline create state
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const [showAuthModal, setShowAuthModal] = useState(false);

  const dropdownRef = useRef(null);
  const dotsButtonRef = useRef(null);
  const inputRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        dotsButtonRef.current && !dotsButtonRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
        setShowCreateInput(false);
        setNewListName('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Focus input when create form opens
  useEffect(() => {
    if (showCreateInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCreateInput]);

  const fetchLists = async () => {
    setListsLoading(true);
    try {
      const [indexData, membershipData] = await Promise.all([
        fundingListsAPI.getListIndex(),
        fundingListsAPI.getListMembership(fundingLookup),
      ]);
      setLists(indexData.results || indexData);
      setMemberLists(new Set(membershipData.list_ids));
    } catch (err) {
      console.error('Failed to fetch lists:', err);
    } finally {
      setListsLoading(false);
    }
  };

  // Heart click — toggles default (Saved) list
  const handleHeartClick = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setSaveLoading(true);
    try {
      if (isSaved) {
        await fundingListsAPI.unsaveOpportunity(fundingLookup);
        setIsSaved(false);
        posthog.capture('funding_opportunity_saved', { action: 'unsaved', funding_id: funding.id, funding_name: funding.name });
        if (onSaveToggle) onSaveToggle(funding.id, false);
      } else {
        await fundingListsAPI.saveOpportunity(fundingLookup);
        setIsSaved(true);
        posthog.capture('funding_opportunity_saved', { action: 'saved', funding_id: funding.id, funding_name: funding.name });
        if (onSaveToggle) onSaveToggle(funding.id, true);
      }
    } catch (err) {
      console.error('Error toggling save:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  // Three-dot click — open dropdown and load lists
  const handleDotsClick = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (!dropdownOpen && dotsButtonRef.current) {
      const rect = dotsButtonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      fetchLists();
    }
    setDropdownOpen(prev => !prev);
    setShowCreateInput(false);
    setNewListName('');
  };

  // Toggle scheme in/out of a specific list
  const handleListToggle = async (e, list) => {
    e.stopPropagation();
    setActionLoading(list.id);
    try {
      if (memberLists.has(list.id)) {
        await fundingListsAPI.removeFromList(fundingLookup, list.id);
        setMemberLists(prev => {
          const next = new Set(prev);
          next.delete(list.id);
          return next;
        });
        posthog.capture('funding_added_to_list', { action: 'removed', funding_id: funding.id, list_id: list.id, list_name: list.name });
        // If this was the default list, sync the heart
        if (list.is_default) {
          setIsSaved(false);
          if (onSaveToggle) onSaveToggle(funding.id, false);
        }
      } else {
        await fundingListsAPI.addToList(fundingLookup, list.id);
        setMemberLists(prev => new Set([...prev, list.id]));
        posthog.capture('funding_added_to_list', { action: 'added', funding_id: funding.id, list_id: list.id, list_name: list.name });
        if (list.is_default) {
          setIsSaved(true);
          if (onSaveToggle) onSaveToggle(funding.id, true);
        }
      }
    } catch (err) {
      console.error('Error toggling list membership:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Create a new list (name only — user then adds scheme separately)
  const handleCreateList = async (e) => {
    e.stopPropagation();
    const name = newListName.trim();
    if (!name) return;
    setCreateLoading(true);
    try {
      const created = await fundingListsAPI.createList(name);
      posthog.capture('funding_list_created', { list_name: name });
      setLists(prev => [...prev, created]);
      setNewListName('');
      setShowCreateInput(false);
    } catch (err) {
      console.error('Failed to create list:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') handleCreateList(e);
    if (e.key === 'Escape') {
      setShowCreateInput(false);
      setNewListName('');
    }
  };

  return (
    <>
      <div
        className="flex items-center"
        onClick={e => e.stopPropagation()}
      >
        {/* Heart button */}
        <Tooltip text={isSaved ? 'Unfavourite' : 'Favourite'}>
          <button
            onClick={handleHeartClick}
            disabled={saveLoading}
            className={`p-1.5 rounded transition-colors ${
              isSaved ? 'text-red-500 hover:text-red-600' : 'text-gray-400 hover:text-red-400'
            } ${saveLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <svg
              className="h-5 w-5"
              fill={isSaved ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>
        </Tooltip>

        {/* Three-dot button */}
        <Tooltip text="Add to list">
          <button
            ref={dotsButtonRef}
            onClick={handleDotsClick}
            className="p-1.5 rounded text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
        </Tooltip>

        {/* Dropdown — rendered in a portal so it escapes any overflow:hidden ancestor */}
        {dropdownOpen && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="bg-white border border-gray-200 rounded-lg shadow-lg"
            style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right, minWidth: '220px', zIndex: 9999 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Add to list
              </p>
            </div>

            {/* List items */}
            <div className="py-1 max-h-52 overflow-y-auto">
              {listsLoading ? (
                <div className="px-3 py-3 text-xs text-gray-400 text-center">Loading...</div>
              ) : lists.length === 0 ? (
                <div className="px-3 py-3 text-xs text-gray-400 text-center">No lists yet</div>
              ) : (
                lists.map(list => {
                  const inList = memberLists.has(list.id);
                  const isActioning = actionLoading === list.id;
                  return (
                    <button
                      key={list.id}
                      onClick={(e) => handleListToggle(e, list)}
                      disabled={isActioning}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <span className="truncate" style={{ color: '#222222' }}>
                          {list.is_default ? 'Favourites' : list.name}
                        </span>
                      </span>
                      <span className="flex-shrink-0 ml-2">
                        {isActioning ? (
                          <svg className="h-3.5 w-3.5 text-gray-300 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : inList ? (
                          <svg className="h-3.5 w-3.5" style={{ color: '#615DEC' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-3.5 w-3.5 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                          </svg>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Create new list */}
            <div className="border-t border-gray-100">
              {showCreateInput ? (
                <div className="px-3 py-2 flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newListName}
                    onChange={e => setNewListName(e.target.value)}
                    onKeyDown={handleCreateKeyDown}
                    placeholder="List name..."
                    className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-gray-400"
                    maxLength={80}
                  />
                  <button
                    onClick={handleCreateList}
                    disabled={!newListName.trim() || createLoading}
                    className="text-xs font-semibold px-2 py-1 rounded transition-colors disabled:opacity-40"
                    style={{ backgroundColor: '#615DEC', color: 'white' }}
                  >
                    {createLoading ? '...' : 'Create'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowCreateInput(true); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  New list
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        action="save funding opportunities"
      />
    </>
  );
};

export default SaveButton;